import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Label,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  FormGroup,
  Input,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomSelect from "components/Common/custom-select";
import Breadcrumbs from "components/Common/Breadcrumb";
import CustomDatePicker from "common/CustomDatePicker";
import TableContainer from "components/Common/TableContainer";
import { useLocation, useHistory } from "react-router-dom";
import getAccessRights from "components/Common/getAccessRights";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { exportEmployeeToExcel } from "pages/exports/exportExcel";
import { exportEmployeeToPDF } from "pages/exports/exportPDF";
import { fetchCategories, fetchDepartments, fetchDesignations, fetchEmployeeDropdownAutoList, fetchLateAttendanceReport, resetAttendanceReport } from "store/actions";
import CustomMonthPicker from "common/CustomMonthPicker";
import dayjs from "dayjs";
import Loader from "common/pageLoader";
import { getAttendanceSalaryDate } from "common/attendanceSalaryDate";

const AttendanceReportLateInOut = () => {
  const [accessRight, setAccessRight] = useState(null);
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const rights = getAccessRights(location.pathname, history);
    setAccessRight(rights?.data);
  }, [location]);
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((prev) => !prev);
  const {
    employeeReport,
    categories,
    departments,
    dropdownAutoList,
    designations,
    loading,
    error,
    success
  } = useSelector((state) => ({
    employeeReport: state.AttendanceReportReducer?.employeeReport ?? [],
    categories: state.CategoryMaster?.categories ?? [],
    departments: state.DepartmentMaster?.departments ?? [],
    dropdownAutoList: state.EmployeeDetailsReducer?.dropdownAutoList ?? [],
    designations: state.DesignationMaster?.designations ?? [],

    loading: state.AttendanceReportReducer?.loading,
    error: state.AttendanceReportReducer?.error,
    success: state.AttendanceReportReducer?.success,
  }));

  useEffect(() => {
      if (authDetails) {
        getAttendanceSalaryDate(authDetails.unit_id).then(response => {
          if (response.status === "success" && response.data.length > 0) {
            const data = response.data[0];
            formik.setFieldValue("fromDate", data.from_date.split('/').reverse().join('-'));
            formik.setFieldValue("toDate", data.to_date.split('/').reverse().join('-'));
          }
        });
      }
    }, [authDetails]);

  useEffect(() => {
    dispatch(resetAttendanceReport())
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchCategories(unitId));
    dispatch(fetchDepartments(unitId));
    dispatch(fetchEmployeeDropdownAutoList(unitId))
    dispatch(fetchDesignations(unitId));
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      reportType: "2", // Default Daily Mode
      month: "",
      fromDate: "",
      toDate: "",
      department: "",
      designation: "",
      category: "",
      employee: ""
    },
    validationSchema: Yup.object().shape({
      reportType: Yup.string().required("Select Report Type"),

      month: Yup.string()
        .nullable()
        .when("reportType", {
          is: "1",
          then: (schema) => schema.required("Month is required"),
          otherwise: (schema) => schema.nullable(),
        }),

      fromDate: Yup.date()
        .nullable()
        .when("reportType", {
          is: "2",
          then: (schema) =>
            schema
              .required("From Date is required")
              .typeError("Invalid Date"),
          otherwise: (schema) => schema.nullable(),
        }),

      toDate: Yup.date()
        .nullable()
        .when("reportType", {
          is: "2",
          then: (schema) =>
            schema
              .required("To Date is required")
              .min(Yup.ref("fromDate"), "To Date must be greater than or equal to From Date")
              .typeError("Invalid Date"),
          otherwise: (schema) => schema.nullable(),
        }),
    }),
    onSubmit: (values) => {
      // console.log("Form values:", values);

      const formData = new FormData();
      let fromDate = values.fromDate;
      let toDate = values.toDate;

      // If Monthly Report → Auto set Start & End of Month
      if (values.reportType === "1" && values.month) {
        const startOfMonth = dayjs(values.month).startOf("month");
        const endOfMonth = dayjs(values.month).endOf("month");

        fromDate = startOfMonth.format("YYYY-MM-DD");
        toDate = endOfMonth.format("YYYY-MM-DD");
      }

      // Common fields
      formData.append("dep_id", values.department || 0);
      formData.append("desig_id", values.designation || 0);
      formData.append("cat_id", values.category || 0);
      formData.append("emp_code", values.employee || 0);
      formData.append("unit_id", authDetails?.unit_id);
      formData.append("create_by", authDetails?.user_id);
      formData.append("from_dt", fromDate);
      formData.append("to_dt", toDate);
      formData.append("source", values.reportType);

      // // Debug check
      // console.log("API FormData: ", {
      //   reportType: values.reportType,
      //   from_date: fromDate,
      //   to_date: toDate,
      //   month: values.month,
      // });

      dispatch(fetchLateAttendanceReport(formData));
    },
  });

  const dailyColumns = [
    { Header: "S.No", Cell: ({ row }) => employeeReport.length - row.index, className: "serial-no text-center", applyHeaderClass: true },
    { Header: "Code", accessor: "emp_no" },
    { Header: "Name", accessor: "emp_name" },
    { Header: "Category", accessor: "category" },
    { Header: "Department", accessor: "dep_name" },
    { Header: "Designation", accessor: "designation" },
    // { Header: "Shift", accessor: "shift_name" },
    { Header: "Shift Timing", accessor: "shift_time" },
    { Header: "Punch Date", accessor: "punch_date" },
    { Header: "Punch In", accessor: "punch_in" },
    { Header: "Punch Out", accessor: "punch_out" },
    { Header: "Actual In", accessor: "actual_in" },
    { Header: "Actual Out", accessor: "actual_out" },
    { Header: "Late In", accessor: "in_late" },
    { Header: "Late Out", accessor: "late_out" },
    { Header: "Early Out", accessor: "early_out" },
    { Header: "Total Punch hours", accessor: "total_hours" },
  ];

  const monthlyColumns = [
    { Header: "S.No", Cell: ({ row }) => employeeReport.length - row.index, className: "serial-no text-center", applyHeaderClass: true },
    { Header: "Code", accessor: "emp_code" },
    { Header: "Name", accessor: "emp_name" },
    { Header: "Category", accessor: "category" },
    { Header: "Department", accessor: "department" },
    { Header: "Designation", accessor: "designation" },
    { Header: "OD", accessor: "on_duty" },
    // { Header: "Permission", accessor: "permission" },
    { Header: "Late In", accessor: "morning_late" },
    // { Header: "Lunch Late In", accessor: "lunch_late" },
    { Header: "Late out", accessor: "late_out" },
    { Header: "Early out", accessor: "early_out" },
    { Header: "Total Early Out Hours", accessor: "total_early_out" },
    { Header: "Total Late In Hours", accessor: "total_late_in_hours" },
    { Header: "Total Late Out Hours", accessor: "total_late_out_hours" },
  ];

  const columns =
    formik.values.reportType === "2"
      ? dailyColumns
      : monthlyColumns;

  const handleMonthSelect = (val) => {
    const date = new Date(val);
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    formik.setFieldValue("fromDate", start.toISOString().split("T")[0]);
    formik.setFieldValue("toDate", end.toISOString().split("T")[0]);
  };

  const handleReset = () => {
    formik.resetForm();
    dispatch(resetAttendanceReport());
  };

  return (
    <div className="page-content">
      <Loader loading={loading} />
      <Container fluid>
        <Breadcrumbs title="Attendance" breadcrumbItem="Late IN & Out" />
        <Card>
          <CardBody>
            <form onSubmit={formik.handleSubmit}>
              <Row className="gy-3">
                <Col md={3}>
                  <Label>Category</Label>
                  <CustomSelect
                    id="category"
                    name="category"
                    options={categories.map(cat => ({ value: cat.empCatgryid, label: cat.empCategory }))}
                    value={
                      categories
                        .map(cat => ({ value: cat.empCatgryid, label: cat.empCategory }))
                        .find(cat => cat.value === formik.values.category) || null
                    }
                    onChange={val => formik.setFieldValue('category', val ? val.value : '')}
                    onBlur={formik.handleBlur}
                    className={`select-dropdown ${formik.touched.category && formik.errors.category ? 'is-invalid' : ''}`}
                  />
                </Col>

                <Col md={3}>
                  <Label>Department</Label>
                  <CustomSelect
                    id="department"
                    name="department"
                    options={departments.map(dept => ({ value: dept.departmentId, label: dept.departmentname }))}
                    value={
                      departments
                        .map(dept => ({ value: dept.departmentId, label: dept.departmentname }))
                        .find(dept => dept.value === formik.values.department) || null
                    }
                    onChange={val => formik.setFieldValue('department', val ? val.value : '')}
                    onBlur={formik.handleBlur}
                    className={`select-dropdown ${formik.touched.department && formik.errors.department ? 'is-invalid' : ''}`}
                  />
                </Col>

                <Col md={3}>
                  <Label>Designation</Label>
                  <CustomSelect
                    id="designation"
                    name="designation"
                    options={designations.map(des => ({ value: des.desig_id, label: des.designation }))}
                    value={
                      designations
                        .map(des => ({ value: des.desig_id, label: des.designation }))
                        .find(des => des.value === formik.values.designation) || null
                    }
                    onChange={(val) => formik.setFieldValue("designation", val ? val.value : "")}
                    onBlur={formik.handleBlur}
                    className={`select-dropdown ${formik.touched.designation && formik.errors.designation ? "is-invalid" : ""
                      }`}
                  />
                </Col>
                <Col md={3}>
                  <Label>
                    Employee
                  </Label>
                  <CustomSelect
                    id="employee"
                    name="employee"
                    options={dropdownAutoList.map(emp => ({
                      value: String(emp.emp_id),
                      label: emp.emp_no
                    }))}

                    // UI needs the object, so find matching option
                    value={
                      dropdownAutoList
                        .map(emp => ({
                          value: String(emp.emp_id),
                          label: emp.emp_no
                        }))
                        .find(option => option.value === formik.values.employee) || null
                    }

                    // Form Data: store string only
                    onChange={(selected) =>
                      formik.setFieldValue("employee", selected ? selected.value : "")
                    }
                  />
                  {formik.touched.employee && formik.errors.employee && (
                    <div className="text-danger">
                      {formik.errors.employee}
                    </div>
                  )}
                </Col>
                <Col md={3}>
                  <Label></Label>
                  <div className="d-flex align-items-center gap-4 mt-2">

                    <Label className="mb-0">
                      <Input
                        type="radio"
                        name="reportType"
                        value="2"
                        checked={formik.values.reportType === "2"}
                        onChange={(e) => {
                          formik.handleChange(e);
                          formik.setFieldValue("month", "");
                          dispatch(resetAttendanceReport())
                        }}
                      />{" "}
                      Period
                    </Label>

                    <Label className="mb-0">
                      <Input
                        type="radio"
                        name="reportType"
                        value="1"
                        checked={formik.values.reportType === "1"}
                        onChange={(e) => {
                          formik.handleChange(e);
                          formik.setFieldValue("fromDate", "");
                          formik.setFieldValue("toDate", "");
                          dispatch(resetAttendanceReport())
                        }}
                      />{" "}
                      Monthly
                    </Label>

                  </div>
                </Col>

                {formik.values.reportType === "1" ? (
                  <Col md={3} >
                    <FormGroup>
                      <Label for="month">
                        Month <span className="text-danger">*</span>
                      </Label>

                      <CustomMonthPicker
                        name="month"
                        value={formik.values.month}
                        onChange={(val) => formik.setFieldValue("month", val)}
                        onBlur={formik.handleBlur}
                        disableFuture={true}
                        className={`form-control ${formik.touched.month && formik.errors.month ? "is-invalid" : ""}`}
                      />

                      {formik.touched.month && formik.errors.month && (
                        <div className="text-danger">{formik.errors.month}</div>
                      )}
                    </FormGroup>
                  </Col>
                ) : (
                  <>
                    <Col md={3}>
                      <Label>
                        From Date <span className="text-danger">*</span>
                      </Label>
                      <CustomDatePicker
                        name="fromDate"
                        value={formik.values.fromDate}
                        onChange={(val) => formik.setFieldValue("fromDate", val)}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.fromDate && formik.errors.fromDate && (
                        <div className="text-danger">{formik.errors.fromDate}</div>
                      )}
                    </Col>

                    <Col md={3}>
                      <Label>
                        To Date <span className="text-danger">*</span>
                      </Label>
                      <CustomDatePicker
                        name="toDate"
                        value={formik.values.toDate}
                        onChange={(val) => formik.setFieldValue("toDate", val)}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.toDate && formik.errors.toDate && (
                        <div className="text-danger">{formik.errors.toDate}</div>
                      )}
                    </Col>
                  </>
                )}

                <Col className="d-flex mob-center align-items-center justify-content-end">
                  <Button type="submit" className="btn button-search me-2 mt-2">
                    <i className="fas fa-search me-1" /> Search
                  </Button>
                  <Button type="button" className="btn button-cancel me-2 mt-2" onClick={handleReset}>
                    <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                  {employeeReport && employeeReport.length > 0 && (
                    <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down">
                      <DropdownToggle color="primary" className="btn button-export mt-2">
                        <i className="fas fa-file-export me-1" /> Export
                      </DropdownToggle>

                      <DropdownMenu>
                        <DropdownItem onClick={() => exportEmployeeToExcel(employeeReport, authDetails?.unit_fullname, authDetails?.unit_address, "Shift Late Report")}>
                          <i className="fas fa-file-excel me-2 text-success" /> Export to Excel
                        </DropdownItem>

                        <DropdownItem onClick={() => exportEmployeeToPDF(employeeReport, authDetails?.unit_fullname, authDetails?.unit_address, "Shift Late Report", columns)}>
                          <i className="fas fa-file-pdf me-2 text-danger" /> Export to PDF
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  )}
                </Col>
              </Row>
            </form>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <TableContainer
              columns={columns}
              data={employeeReport}
              isGlobalFilter={false}
              customPageSize={10}
              tableSorter={false}
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default AttendanceReportLateInOut;
